import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Building, Calendar, Check, Clock, CreditCard, FileText, GraduationCap, IndianRupee, Mail, Monitor, Phone, Upload, User, Users, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import qrCodeImage from '../assets/images/QR code gpay.jpeg';
import logo from '../logo/lo.png';

interface FormData {
  name: string;
  email: string;
  phone: string;
  collegeName: string;
  degree: string;
  yearOfPassout: string;
  course: string;
  sessionCategory: 'group' | 'one-to-one' | '';
  paymentScreenshot: File | null;
  paymentType: 'before_placement' | 'after_placement';
  paymentAmount: number;
  agreesToTerms: boolean;
  balancePaymentTimeline: string;
}

interface FormErrors {
  [key: string]: string;
}

interface PaymentHistory {
  beforePlacement: number;
  afterPlacement: number;
  totalPaid: number;
  remainingBalance: number;
  isFullyPaid: boolean;
  hasCompletedSlotSelection: boolean;
  isFirstTimeUser: boolean;
}

interface SubmissionResult {
  status: 'success' | 'error';
  message?: string;
  success?: boolean;
  paymentHistory?: PaymentHistory;
}

interface SlotData {
  sessionType: 'morning' | 'afternoon' | 'evening' | '';
  selectedSlot: string;
  mode: 'online' | 'offline' | '';
}

const getCourseFees = (course: string, sessionCategory: 'group' | 'one-to-one' | '' = '') => {
  if (sessionCategory === 'one-to-one') return { beforeFee: 15000, afterFee: 15000, totalFee: 30000 };
  return { beforeFee: 10000, afterFee: 10000, totalFee: 20000 };
};

// ── Shared dark-theme style helpers ──────────────────────────────────────────
const inp = (err?: string): React.CSSProperties => ({
  width: '100%',
  padding: '11px 12px 11px 36px',
  borderRadius: '10px',
  border: `1.5px solid ${err ? '#f87171' : 'rgba(255,255,255,0.1)'}`,
  background: err ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.05)',
  color: err ? '#fca5a5' : 'white',
  fontSize: '0.88rem',
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
});

const inpNoIcon = (err?: string): React.CSSProperties => ({
  ...inp(err),
  padding: '11px 12px',
});

const sel = (err?: string): React.CSSProperties => ({
  ...inp(err),
  cursor: 'pointer',
  appearance: 'none',
  background: err ? 'rgba(248,113,113,0.08)' : '#1a1530',
  paddingRight: '2.5rem',
});

const lbl: React.CSSProperties = {
  display: 'block',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.7rem',
  fontWeight: '700',
  marginBottom: '7px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const errTxt: React.CSSProperties = {
  color: '#f87171',
  fontSize: '0.72rem',
  marginTop: '5px',
  fontWeight: '600',
};

const glassCard: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '22px',
  padding: '32px',
  backdropFilter: 'blur(24px)',
};

const innerSection: React.CSSProperties = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  padding: '18px',
};

const StudentRegistrationForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<'registration' | 'slot-selection' | 'completion'>('registration');
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    collegeName: '',
    degree: '',
    yearOfPassout: '',
    course: '',
    sessionCategory: '',
    paymentScreenshot: null,
    paymentType: 'before_placement',
    paymentAmount: 0,
    agreesToTerms: false,
    balancePaymentTimeline: ''
  });

  const [slotData, setSlotData] = useState<SlotData>({
    sessionType: '',
    selectedSlot: '',
    mode: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory | null>(null);
  const [checkingHistory, setCheckingHistory] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const courseOptions = [
    { value: '', label: 'Select a course' },
    { value: 'AIML', label: 'AI & ML Development' },
    { value: 'business-analytics', label: 'Business Analytics' },
    { value: 'full-stack-java', label: 'Full Stack Java Development' },
    { value: 'Python full-stack development', label: 'Python Full Stack Development' },
    { value: 'Data Science', label: 'Data Science' },
    { value: 'Data Analytics', label: 'Data Analytics' },
  ];

  const { beforeFee: BEFORE_PLACEMENT_FEE, afterFee: AFTER_PLACEMENT_FEE, totalFee: TOTAL_COURSE_FEE } = getCourseFees(formData.course, formData.sessionCategory);

  const termsAndConditions = [
    "The duration of the course is 3 month validity",
    "Students are required to complete all weekly assessments. Unexcused absences from these assessments will be subject to appropriate action.",
    "Following the initial payment, the remaining balance is due in full on the program's official start date. A 48-hour grace period is granted for any outstanding balance.",
    "All official updates and program information will be disseminated via email. Students are responsible for checking their email regularly. The management is not responsible for missed communications due to unchecked email accounts.",
    "Students are required to provide timely updates on the completion of their daily tasks, adhering to established deadlines.",
    "Students experiencing difficulty comprehending new concepts are required to notify the management within 48 hours of the concept's introduction. Failure to do so within this timeframe absolves the management of responsibility for addressing the learning challenges.",
    "Internship will be only scheduled depends upon your weekly assessment performance",
    "Consistent performance below expectations in assessments may necessitate an extended timeframe for program completion.",
    "Consistent communication with the management is mandatory throughout the duration of each project, beginning with the project's start. The management will not be held accountable for errors or necessary updates identified post-project completion if regular progress updates have not been provided",
    "Any instance of academic dishonesty or malpractice during assessments will be subject to disciplinary action, up to and including termination from the program.",
    "Attendance at all scheduled interviews is mandatory. Failure to attend will result in consequences as determined by the program.",
    "Students are required to sign the daily work done sheet only upon demonstrating comprehension of the daily topics covered.",
    "Students will be certified as having completed the course only upon successful completion and submission of all required daily tasks, weekly assessments and projects. Fulfilment of this requirement is mandatory for course completion.",
    `If the student secures a job through Vista Tech's placement support, the remaining ₹${AFTER_PLACEMENT_FEE.toLocaleString('en-IN')} must be paid within 10 working days of receiving the offer letter. Failure to pay the remaining amount after placement may result in certificate withholding and potential legal action.`,
    "Internship and Live Project enable to the candidates depends upon their academic performance",
    "In weekly assessment performance, students must obtain a minimum of 50 marks.",
    "Placement support includes resume building, mock interviews, referrals, and interview scheduling.",
    "Two consecutive days of leave are not permitted in this program. Any leave requests must be submitted with prior notice",
    "Once the timing of the batch is finalized, it will be never changed according to the candidates",
    "Prior to the commencement of class, students are requested to arrange themselves comfortably, regardless of the class format (online or offline)",
    "For the duration of online classes, students are expected to remain present. Leaving the online classroom before the scheduled end time will result in an absence being recorded.",
    `The initial ₹${BEFORE_PLACEMENT_FEE.toLocaleString('en-IN')} is non-refundable under any circumstances. No refund or fee adjustments will be entertained once the training begins.`,
    "Upon fulfillment of the aforementioned terms and circumstances, the firm offers a 100% placement",
    "Violation of any of the aforementioned terms and conditions may result in the immediate cancellation of your refund, at the discretion of the company, based on the severity of the infraction and the explanation provided"
  ];

  const checkPaymentHistory = async () => {
    if (!formData.email) return;
    setCheckingHistory(true);
    try {
      const scriptUrl = 'https://script.google.com/macros/s/AKfycbx9SY60JbySriOxeNMnvpV-xp_0jkq8GNFVJJlH3tZcIusOl2WMTMfiPam_p8kNcASTAA/exec';
      const response = await fetch(`${scriptUrl}?action=checkPaymentHistory&email=${encodeURIComponent(formData.email)}&course=${encodeURIComponent(formData.course)}&sessionCategory=${encodeURIComponent(formData.sessionCategory)}`, {
        method: 'GET',
        mode: 'cors'
      });
      const result = await response.json();
      if (result.status === 'success' && result.paymentHistory) {
        const history = result.paymentHistory;
        const isFirstTime = history.totalPaid === 0;
        const hasCompletedSlotSelection = history.hasCompletedSlotSelection || false;
        setIsFirstTimeUser(isFirstTime);
        setPaymentHistory({
          ...history,
          remainingBalance: Math.max(0, TOTAL_COURSE_FEE - (history.totalPaid || 0)),
          hasCompletedSlotSelection,
          isFirstTimeUser: isFirstTime
        });
        if (history.beforePlacement >= BEFORE_PLACEMENT_FEE) {
          setFormData(prev => ({
            ...prev,
            paymentType: 'after_placement',
            paymentAmount: Math.max(0, AFTER_PLACEMENT_FEE - history.afterPlacement),
            agreesToTerms: !isFirstTime
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            paymentType: 'before_placement',
            paymentAmount: isFirstTime ? 1000 : Math.max(0, BEFORE_PLACEMENT_FEE - history.beforePlacement)
          }));
        }
      } else {
        setIsFirstTimeUser(true);
        setPaymentHistory({
          beforePlacement: 0,
          afterPlacement: 0,
          totalPaid: 0,
          remainingBalance: TOTAL_COURSE_FEE,
          isFullyPaid: false,
          hasCompletedSlotSelection: false,
          isFirstTimeUser: true
        });
        setFormData(prev => ({ ...prev, paymentAmount: 1000 }));
      }
    } catch (error) {
      console.error('Error checking payment history:', error);
    } finally {
      setCheckingHistory(false);
    }
  };

  useEffect(() => {
    if (formData.email && formData.email.includes('@')) {
      checkPaymentHistory();
    }
  }, [formData.email]);

  useEffect(() => {
    if (!formData.course && !formData.sessionCategory) return;
    if (formData.email && formData.email.includes('@')) {
      checkPaymentHistory();
    } else if (paymentHistory) {
      const { totalFee, beforeFee, afterFee } = getCourseFees(formData.course, formData.sessionCategory);
      const beforePaid = paymentHistory.beforePlacement;
      const afterPaid = paymentHistory.afterPlacement;
      setPaymentHistory(prev => prev ? { ...prev, remainingBalance: totalFee - prev.totalPaid } : null);
      setFormData(prev => ({
        ...prev,
        paymentAmount: prev.paymentType === 'before_placement'
          ? Math.max(0, beforeFee - beforePaid)
          : Math.max(0, afterFee - afterPaid)
      }));
    }
  }, [formData.course, formData.sessionCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) newErrors.phone = 'Please enter a valid 10-digit phone number';
    if (!formData.collegeName.trim()) newErrors.collegeName = 'College name is required';
    if (!formData.degree.trim()) newErrors.degree = 'Degree is required';
    if (!formData.yearOfPassout.trim()) newErrors.yearOfPassout = 'Year of passout is required';
    else {
      const year = parseInt(formData.yearOfPassout);
      const currentYear = new Date().getFullYear();
      if (year < 1950 || year > currentYear + 4) newErrors.yearOfPassout = 'Please enter a valid year';
    }
    if (!formData.course) newErrors.course = 'Please select a course';
    if (!formData.sessionCategory) newErrors.sessionCategory = 'Please select a session type';
    if (isFirstTimeUser && !formData.agreesToTerms) newErrors.agreesToTerms = 'You must agree to the terms and conditions';
    if (paymentCompleted && !formData.paymentScreenshot && formData.paymentAmount > 0) newErrors.paymentScreenshot = 'Payment screenshot is required';
    if (formData.paymentAmount <= 0 && !paymentHistory?.isFullyPaid) newErrors.paymentAmount = 'Payment amount must be greater than 0';
    if (!isFirstTimeUser && formData.paymentAmount > 0) {
      const expectedBalance = formData.paymentType === 'before_placement'
        ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
        : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0));
      if (formData.paymentAmount < expectedBalance && !formData.balancePaymentTimeline) {
        newErrors.balancePaymentTimeline = 'Please select when you will pay the remaining balance';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePaymentTypeChange = (type: 'before_placement' | 'after_placement') => {
    setFormData(prev => ({
      ...prev,
      paymentType: type,
      paymentAmount: type === 'before_placement'
        ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
        : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0))
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, paymentScreenshot: file }));
    if (errors.paymentScreenshot) setErrors(prev => ({ ...prev, paymentScreenshot: '' }));
  };

  const handlePaymentClick = () => {
    if (validateForm()) {
      if (paymentHistory?.isFullyPaid) {
        alert('You have already completed all payments for this course!');
        return;
      }
      setShowPayment(true);
    }
  };

  const copyUpiId = () => {
    navigator.clipboard.writeText('9894449002@okbizaxis');
    alert('UPI ID copied to clipboard!');
  };
  const copyText = (text: string, label: string) => { navigator.clipboard.writeText(text); alert(`${label} copied!`); };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = error => reject(error);
    });
  };

  const submitToGoogleSheets = async (data: FormData): Promise<SubmissionResult> => {
    const scriptUrl = 'https://script.google.com/macros/s/AKfycbx9SY60JbySriOxeNMnvpV-xp_0jkq8GNFVJJlH3tZcIusOl2WMTMfiPam_p8kNcASTAA/exec';
    try {
      let response;
      let result;
      if (data.paymentScreenshot && data.paymentAmount > 0) {
        const base64Data = await fileToBase64(data.paymentScreenshot);
        const urlEncodedData = new URLSearchParams();
        urlEncodedData.append('action', 'submitPayment');
        urlEncodedData.append('name', data.name);
        urlEncodedData.append('email', data.email);
        urlEncodedData.append('phone', data.phone);
        urlEncodedData.append('collegeName', data.collegeName);
        urlEncodedData.append('degree', data.degree);
        urlEncodedData.append('yearOfPassout', data.yearOfPassout);
        urlEncodedData.append('course', data.course);
        urlEncodedData.append('sessionCategory', data.sessionCategory);
        urlEncodedData.append('paymentType', data.paymentType);
        urlEncodedData.append('paymentAmount', data.paymentAmount.toString());
        urlEncodedData.append('agreesToTerms', data.agreesToTerms.toString());
        urlEncodedData.append('timestamp', new Date().toISOString());
        urlEncodedData.append('paymentScreenshotBase64', base64Data);
        urlEncodedData.append('screenshotFilename', data.paymentScreenshot.name);
        urlEncodedData.append('screenshotMimeType', data.paymentScreenshot.type);
        urlEncodedData.append('isFirstTimeUser', isFirstTimeUser.toString());
        if (data.balancePaymentTimeline) urlEncodedData.append('balancePaymentTimeline', data.balancePaymentTimeline);
        response = await fetch(scriptUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: urlEncodedData.toString(), mode: 'cors' });
        result = await response.text();
      } else {
        const urlEncodedData = new URLSearchParams();
        urlEncodedData.append('action', 'submitRegistration');
        urlEncodedData.append('name', data.name);
        urlEncodedData.append('email', data.email);
        urlEncodedData.append('phone', data.phone);
        urlEncodedData.append('collegeName', data.collegeName);
        urlEncodedData.append('degree', data.degree);
        urlEncodedData.append('yearOfPassout', data.yearOfPassout);
        urlEncodedData.append('course', data.course);
        urlEncodedData.append('sessionCategory', data.sessionCategory);
        urlEncodedData.append('agreesToTerms', data.agreesToTerms.toString());
        urlEncodedData.append('timestamp', new Date().toISOString());
        urlEncodedData.append('isFirstTimeUser', isFirstTimeUser.toString());
        response = await fetch(scriptUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: urlEncodedData.toString(), mode: 'cors' });
        result = await response.text();
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      try {
        const jsonResult = JSON.parse(result);
        return { status: jsonResult.status || 'success', message: jsonResult.message || 'Data submitted successfully', success: jsonResult.status === 'success', paymentHistory: jsonResult.paymentHistory };
      } catch {
        return { status: 'success', success: true, message: 'Form submitted successfully' };
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        return { status: 'error', message: 'Network error: Please check your internet connection and try again.', success: false };
      }
      return { status: 'error', message: error instanceof Error ? error.message : 'Unknown error occurred', success: false };
    }
  };

  const submitSlotRequest = async () => {
    if (!slotData.sessionType || !slotData.mode) {
      alert('Please select session type, time slot, and mode');
      return;
    }
    setIsSubmitting(true);
    try {
      const scriptUrl = 'https://script.google.com/macros/s/AKfycbx9SY60JbySriOxeNMnvpV-xp_0jkq8GNFVJJlH3tZcIusOl2WMTMfiPam_p8kNcASTAA/exec';
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('action', 'submitSlotRequest');
      urlEncodedData.append('name', formData.name);
      urlEncodedData.append('email', formData.email);
      urlEncodedData.append('phone', formData.phone);
      urlEncodedData.append('course', formData.course);
      urlEncodedData.append('sessionCategory', formData.sessionCategory);
      urlEncodedData.append('sessionType', slotData.sessionType);
      urlEncodedData.append('mode', slotData.mode);
      urlEncodedData.append('status', 'pending');
      urlEncodedData.append('timestamp', new Date().toISOString());
      const response = await fetch(scriptUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: urlEncodedData.toString(), mode: 'cors' });
      if (response.ok) {
        setCurrentStep('completion');
        alert('Session preference submitted successfully! You will receive an email once your session is confirmed.');
      } else {
        throw new Error('Failed to submit session preference');
      }
    } catch (error) {
      console.error('Error submitting session preference:', error);
      alert('Error submitting session preference. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (formData.paymentAmount > 0 && !paymentCompleted) {
      alert('Please complete the payment process first');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await submitToGoogleSheets(formData);
      if (result.status === 'success' || result.success) {
        if (result.paymentHistory) setPaymentHistory(result.paymentHistory);
        setRegistrationCompleted(true);
        if (isFirstTimeUser) {
          setCurrentStep('slot-selection');
          alert('Registration completed successfully! Please select your preferred session type.');
        } else {
          setCurrentStep('completion');
          alert('Balance payment completed successfully! Your registration is now complete.');
        }
      } else {
        alert('Error submitting registration: ' + (result.message || 'Please try again.'));
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Error submitting registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Background orbs (same as EnrollForm / DMEnquiryForm) ───────────────────
  const Orbs = () => (
    <>
      {[
        { s: 500, t: '-180px', l: '-150px', c: 'rgba(78,58,105,0.45)' },
        { s: 380, b: '-120px', r: '-80px',  c: 'rgba(240,180,41,0.12)' },
        { s: 260, t: '45%',   l: '58%',     c: 'rgba(255,101,53,0.1)' },
      ].map((o, i) => (
        <motion.div key={i}
          style={{ position: 'absolute', width: o.s, height: o.s, borderRadius: '50%',
            background: `radial-gradient(circle, ${o.c}, transparent 70%)`,
            top: (o as any).t, left: (o as any).l, right: (o as any).r, bottom: (o as any).b,
            pointerEvents: 'none' }}
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 10 + i * 4, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </>
  );

  // ── Terms Modal ─────────────────────────────────────────────────────────────
  const TermsModal = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem', background: 'rgba(13,11,20,0.9)', backdropFilter: 'blur(10px)' }}
    >
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        style={{ background: '#1a1530', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', maxWidth: '720px', width: '100%', maxHeight: '88vh', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.6)', fontFamily: 'Poppins, sans-serif' }}
      >
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', background: 'linear-gradient(135deg, #2b2240, #4e3a69)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} color="white" />
            </div>
            <div>
              <p style={{ color: 'white', fontWeight: '800', fontSize: '0.95rem', margin: 0 }}>Terms & Conditions</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: 0 }}>Vista Tech Pay After Placement Program</p>
            </div>
          </div>
          <button onClick={() => setShowTermsModal(false)}
            style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} color="white" />
          </button>
        </div>
        {/* body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '62vh' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {termsAndConditions.map((term, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.02)' }}>
                <span style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '6px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.65rem', fontWeight: '900', marginTop: '1px' }}>{i + 1}</span>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', lineHeight: 1.65, margin: 0 }}>{term}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '16px', padding: '14px', borderRadius: '12px', background: 'rgba(240,180,41,0.07)', border: '1px solid rgba(240,180,41,0.2)', textAlign: 'center' }}>
            <p style={{ color: '#F0B429', fontSize: '0.78rem', margin: 0 }}>
              Full terms at{' '}
              <a href="https://vistatech.org/Terms-and-Conditions" target="_blank" rel="noopener noreferrer" style={{ fontWeight: '700', color: '#F0B429' }}>
                vistatech.org/Terms-and-Conditions
              </a>
            </p>
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={() => setShowTermsModal(false)}
            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.9rem', color: '#0d0b14', background: 'linear-gradient(135deg, #F0B429, #FF6535)' }}>
            I Understand & Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  // ── Session Selection Page ──────────────────────────────────────────────────
  const SessionSelectionPage = () => (
    <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg, #2b2240 0%, #0d0b14 60%, #1a0b24 100%)', position: 'relative', overflow: 'hidden' }}>
      <Orbs />
      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <img src={logo} alt="Vista Tech" style={{ height: '44px', objectFit: 'contain', marginBottom: '18px' }} />
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', marginBottom: '16px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
            <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#4ade80', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Registration Confirmed</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2rem)', fontWeight: '900', color: 'white', marginBottom: '8px' }}>
            Choose Your{' '}
            <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Schedule</span>
          </motion.h1>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', margin: 0 }}>
            Select your preferred timing for{' '}
            <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: '600' }}>{formData.course}</span>
          </p>
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={glassCard}>
            {/* Session category pill */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px', padding: '10px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {formData.sessionCategory === 'one-to-one' ? <User size={16} color="#F0B429" /> : <Users size={16} color="#F0B429" />}
              <span style={{ color: '#F0B429', fontWeight: '700', fontSize: '0.84rem' }}>
                {formData.sessionCategory === 'one-to-one' ? '1-on-1 Personalized Mentorship' : 'Group Session'} — Slot Preferences
              </span>
            </div>

            {/* Time Slot */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={13} /> Choose Your Time Slot
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px' }}>
                {[
                  { key: 'morning',   label: 'Morning',   sub: 'Weekdays', emoji: '🌅' },
                  { key: 'afternoon', label: 'Afternoon', sub: 'Weekdays', emoji: '☀️' },
                  { key: 'evening',   label: 'Evening',   sub: 'Weekdays', emoji: '🌆' },
                ].map(({ key, label, sub, emoji }) => {
                  const sel2 = slotData.sessionType === key;
                  return (
                    <motion.button key={key} type="button" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.97 }}
                      onClick={() => setSlotData(prev => ({ ...prev, sessionType: key as any, selectedSlot: '' }))}
                      style={{ padding: '16px 12px', borderRadius: '12px', border: `1.5px solid ${sel2 ? '#F0B429' : 'rgba(255,255,255,0.08)'}`, background: sel2 ? 'rgba(240,180,41,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative', boxShadow: sel2 ? '0 0 0 2px rgba(240,180,41,0.2)' : 'none' }}
                    >
                      {sel2 && (
                        <div style={{ position: 'absolute', top: '8px', right: '8px', width: '18px', height: '18px', borderRadius: '50%', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Check size={10} color="white" />
                        </div>
                      )}
                      <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>{emoji}</div>
                      <div style={{ fontWeight: '700', fontSize: '0.84rem', color: sel2 ? '#F0B429' : 'rgba(255,255,255,0.75)' }}>{label}</div>
                      <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>{sub}</div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Mode */}
            {slotData.sessionType && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Monitor size={13} /> Learning Mode
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { key: 'online',  icon: <Monitor size={18} />,  label: 'Online',    sub: 'Join from anywhere',      features: ['Zoom / Google Meet', 'Interactive sessions', 'Recorded for review'], col: '#3b82f6' },
                    { key: 'offline', icon: <Users size={18} />,    label: 'In-Person', sub: 'At our training center',   features: ['Face-to-face learning', 'Hands-on experience', 'Direct mentor access'], col: '#10b981' },
                  ].map(({ key, icon, label, sub, features, col }) => {
                    const active = slotData.mode === key;
                    return (
                      <motion.button key={key} type="button" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                        onClick={() => setSlotData(prev => ({ ...prev, mode: key as any }))}
                        style={{ padding: '18px', borderRadius: '12px', border: `1.5px solid ${active ? col : 'rgba(255,255,255,0.08)'}`, background: active ? `${col}15` : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative', boxShadow: active ? `0 0 0 2px ${col}30` : 'none' }}
                      >
                        {active && (
                          <div style={{ position: 'absolute', top: '10px', right: '10px', width: '18px', height: '18px', borderRadius: '50%', background: col, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={10} color="white" />
                          </div>
                        )}
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: active ? col : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', color: active ? 'white' : 'rgba(255,255,255,0.35)' }}>{icon}</div>
                        <div style={{ fontWeight: '700', fontSize: '0.88rem', color: active ? col : 'rgba(255,255,255,0.75)' }}>{label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>{sub}</div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {features.map(f => (
                            <li key={f} style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: active ? col : 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Summary */}
            {slotData.sessionType && slotData.mode && (
              <div style={{ ...innerSection, marginBottom: '24px' }}>
                <p style={{ ...lbl, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={12} /> Confirmation Summary</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[
                    ['Name', formData.name],
                    ['Course', formData.course],
                    ['Phone', formData.phone],
                    ['Session', formData.sessionCategory === 'one-to-one' ? '1-on-1' : 'Group'],
                    ['Time', slotData.sessionType === 'morning' ? 'Morning' : slotData.sessionType === 'afternoon' ? 'Afternoon' : 'Evening'],
                    ['Mode', slotData.mode.charAt(0).toUpperCase() + slotData.mode.slice(1)],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                      <p style={{ ...lbl, marginBottom: '2px' }}>{k}</p>
                      <p style={{ color: 'white', fontSize: '0.84rem', fontWeight: '600', margin: 0 }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit */}
            <motion.button
              onClick={submitSlotRequest}
              disabled={!slotData.sessionType || !slotData.mode || isSubmitting}
              whileHover={!slotData.sessionType || !slotData.mode || isSubmitting ? {} : { scale: 1.01 }}
              whileTap={!slotData.sessionType || !slotData.mode || isSubmitting ? {} : { scale: 0.98 }}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: !slotData.sessionType || !slotData.mode || isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: !slotData.sessionType || !slotData.mode || isSubmitting ? 'rgba(255,255,255,0.07)' : 'linear-gradient(135deg, #F0B429, #FF6535)', color: !slotData.sessionType || !slotData.mode || isSubmitting ? 'rgba(255,255,255,0.25)' : '#0d0b14', boxShadow: !slotData.sessionType || !slotData.mode || isSubmitting ? 'none' : '0 8px 28px rgba(240,180,41,0.35)' }}
            >
              {isSubmitting ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Confirming...</> : <><Check size={18} /> Confirm Session Preference</>}
            </motion.button>
            <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '12px', margin: '12px 0 0' }}>Preferences reviewed within 24–48 hours · Confirmation sent to your email</p>
          </motion.div>
        </div>
      </div>
    </div>
  );

  // ── Completion Page ─────────────────────────────────────────────────────────
  const CompletionPage = () => (
    <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg, #2b2240 0%, #0d0b14 60%, #1a0b24 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 1.5rem', position: 'relative', overflow: 'hidden' }}>
      <Orbs />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '420px', width: '100%' }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', overflow: 'hidden', backdropFilter: 'blur(24px)', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}
        >
          {/* Banner */}
          <div style={{ padding: '40px 32px', textAlign: 'center', background: isFirstTimeUser ? 'linear-gradient(135deg, #2b2240, #4e3a69)' : 'linear-gradient(135deg, #065f46, #059669)' }}>
            <motion.div animate={{ rotate: [0, -8, 8, -8, 0] }} transition={{ delay: 0.3, duration: 0.7 }}
              style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', boxShadow: '0 0 48px rgba(240,180,41,0.5)' }}>
              <Check size={34} color="white" />
            </motion.div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '900', color: 'white', marginBottom: '6px' }}>{isFirstTimeUser ? 'Request Submitted!' : 'Payment Complete!'}</h1>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.84rem', margin: 0 }}>{isFirstTimeUser ? 'Awaiting admin approval' : 'Balance payment processed'}</p>
          </div>

          <div style={{ padding: '28px 28px' }}>
            {isFirstTimeUser ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                  {['Admin reviews your session preference within 24–48 hours', 'Email notification sent upon approval', 'Class joining details delivered to your inbox'].map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', borderRadius: '10px', background: i === 0 ? 'rgba(240,180,41,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${i === 0 ? 'rgba(240,180,41,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '8px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontSize: '0.65rem', fontWeight: '900' }}>{i + 1}</div>
                      <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', lineHeight: 1.55, margin: 0 }}>{step}</p>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                  {[['Name', formData.name], ['Course', formData.course], ['Session', formData.sessionCategory === 'one-to-one' ? '1-on-1' : 'Group'], ['Time', slotData.sessionType === 'morning' ? 'Morning' : slotData.sessionType === 'afternoon' ? 'Afternoon' : 'Evening'], ['Mode', slotData.mode?.charAt(0).toUpperCase() + slotData.mode?.slice(1)], ['Status', 'Pending']].map(([k, v]) => (
                    <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                      <p style={{ ...lbl, marginBottom: '2px' }}>{k}</p>
                      <p style={{ color: k === 'Status' ? '#fbbf24' : 'white', fontSize: '0.82rem', fontWeight: '600', margin: 0 }}>{v}</p>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)' }}>
                  <span style={{ fontSize: '1.1rem' }}>📬</span>
                  <p style={{ color: '#fbbf24', fontSize: '0.78rem', lineHeight: 1.55, margin: 0 }}>Check your inbox and spam folder regularly for updates from Vista Tech.</p>
                </div>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center', padding: '20px', borderRadius: '14px', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', marginBottom: '16px' }}>
                  <p style={{ fontSize: '2rem', fontWeight: '900', color: '#34d399', margin: '0 0 4px' }}>₹{formData.paymentAmount.toLocaleString('en-IN')}</p>
                  <p style={{ color: '#34d399', fontSize: '0.84rem', fontWeight: '600', margin: 0 }}>Payment Confirmed ✓</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                  {[['Amount Paid', `₹${formData.paymentAmount.toLocaleString('en-IN')}`], ['Payment Type', formData.paymentType.replace('_', ' ')], ['Course', formData.course]].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.04)' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>{k}</span>
                      <span style={{ color: 'white', fontSize: '0.82rem', fontWeight: '600', textTransform: 'capitalize' }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <span style={{ fontSize: '1.1rem' }}>📚</span>
                  <p style={{ color: '#93c5fd', fontSize: '0.78rem', lineHeight: 1.55, margin: 0 }}>Continue attending your scheduled classes. Your instructor will reach out with updates.</p>
                </div>
              </>
            )}
            <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', marginTop: '16px', margin: '16px 0 0' }}>Thank you for choosing Vista Tech · support@vistatech.com</p>
          </div>
        </motion.div>
      </div>
    </div>
  );

  // ── Step routing ────────────────────────────────────────────────────────────
  if (currentStep === 'slot-selection') return <SessionSelectionPage />;
  if (currentStep === 'completion') return <CompletionPage />;

  // ── Main Registration Form ──────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg, #2b2240 0%, #0d0b14 60%, #1a0b24 100%)', position: 'relative', overflow: 'hidden' }}>
      <Orbs />

      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>

        {/* ── Page Header ── */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <img src={logo} alt="Vista Tech" style={{ height: '44px', objectFit: 'contain', marginBottom: '18px' }} />
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.25)', marginBottom: '16px' }}>
            <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }}
              style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F0B429', display: 'inline-block' }} />
            <span style={{ fontSize: '0.68rem', fontWeight: '800', color: '#F0B429', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Vista Tech — Official Enrollment Portal</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: '900', color: 'white', lineHeight: 1.1, marginBottom: '10px' }}>
            50%{' '}
            <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Pay After</span>
            {' '}Placement
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem' }}>
            Launch your tech career — pay half only after you're placed
          </motion.p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginTop: '16px' }}>
            {[['🔒', 'SSL Secure'], ['✓', '100% Verified'], ['⚡', 'Instant Enroll']].map(([icon, label]) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                <span>{icon}</span><span>{label}</span>
              </span>
            ))}
          </div>
        </div>

        {/* ── Form Card ── */}
        <div style={{ maxWidth: '580px', margin: '0 auto' }}>
          <AnimatePresence>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} style={glassCard}>

              {/* New / Returning banner */}
              {paymentHistory && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', marginBottom: '24px', background: isFirstTimeUser ? 'rgba(240,180,41,0.1)' : 'rgba(245,158,11,0.1)', border: `1px solid ${isFirstTimeUser ? 'rgba(240,180,41,0.25)' : 'rgba(245,158,11,0.25)'}` }}>
                  <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }}
                    style={{ width: '8px', height: '8px', borderRadius: '50%', background: isFirstTimeUser ? '#F0B429' : '#f59e0b', flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: '0.82rem', fontWeight: '700', color: isFirstTimeUser ? '#F0B429' : '#fbbf24' }}>
                    {isFirstTimeUser ? '🆕 New Registration — First Time Payment' : '🔄 Returning Student — Balance Payment'}
                  </span>
                </div>
              )}

              {/* ── Course ── */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <GraduationCap size={13} /> Select Your Course
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', zIndex: 1 }}><GraduationCap size={15} /></span>
                  <select name="course" value={formData.course} onChange={handleInputChange}
                    style={sel(errors.course)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.course ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  >
                    {courseOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', fontSize: '0.7rem' }}>▾</span>
                </div>
                {errors.course && <p style={errTxt}>⚠ {errors.course}</p>}
              </div>

              {/* ── Session Type ── */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={13} /> Session Type
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { key: 'group',       icon: <Users size={20} />,  label: 'Group Session',      sub: 'Learn collaboratively', desc: 'Group live sessions will be available with Placement support', col: '#F0B429', grad: 'linear-gradient(135deg,#F0B429,#FF6535)' },
                    { key: 'one-to-one',  icon: <User size={20} />,   label: '1-on-1 Mentorship',  sub: 'Dedicated personal guidance', desc: '1 to 1 Live sessions & A personalised Senior analyst or developer will support you to get placed', col: '#a78bfa', grad: 'linear-gradient(135deg,#4e3a69,#2b2240)' },
                  ].map(({ key, icon, label, sub, desc, col, grad }) => {
                    const active = formData.sessionCategory === key;
                    return (
                      <motion.button key={key} type="button" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.97 }}
                        onClick={() => setFormData(prev => ({ ...prev, sessionCategory: key as any, paymentAmount: 0 }))}
                        style={{ padding: '16px', borderRadius: '12px', border: `1.5px solid ${active ? col : 'rgba(255,255,255,0.08)'}`, background: active ? `${col}12` : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative', boxShadow: active ? `0 0 0 2px ${col}25, 0 4px 16px ${col}18` : 'none' }}
                      >
                        {active && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                            style={{ position: 'absolute', top: '10px', right: '10px', width: '18px', height: '18px', borderRadius: '50%', background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={10} color="white" />
                          </motion.div>
                        )}
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: active ? grad : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', color: active ? 'white' : 'rgba(255,255,255,0.3)' }}>{icon}</div>
                        <div style={{ fontWeight: '700', fontSize: '0.88rem', color: active ? col : 'rgba(255,255,255,0.7)' }}>{label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{sub}</div>
                        <div style={{ fontSize: '0.68rem', color: active ? `${col}cc` : 'rgba(255,255,255,0.3)', marginTop: '6px', lineHeight: 1.5 }}>{desc}</div>
                      </motion.button>
                    );
                  })}
                </div>
                {errors.sessionCategory && <p style={errTxt}>⚠ {errors.sessionCategory}</p>}
              </div>

              {/* ── Email ── */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Mail size={13} /> Email Address</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Mail size={15} /></span>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                    placeholder="you@example.com" style={inp(errors.email)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.email ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.email && <p style={errTxt}>⚠ {errors.email}</p>}
                {checkingHistory && (
                  <p style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.75rem', color: '#F0B429', marginTop: '6px', fontWeight: '600' }}>
                    <span style={{ width: '12px', height: '12px', border: '2px solid #F0B429', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    Checking your payment history...
                  </p>
                )}
              </div>

              {/* ── Payment History ── */}
              {paymentHistory && (
                <div style={{ ...innerSection, marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CreditCard size={16} color="rgba(255,255,255,0.5)" />
                      <span style={{ color: 'white', fontWeight: '700', fontSize: '0.84rem' }}>Payment History</span>
                    </div>
                    {paymentHistory.isFullyPaid && (
                      <span style={{ fontSize: '0.65rem', fontWeight: '800', padding: '3px 10px', borderRadius: '999px', background: 'rgba(52,211,153,0.15)', color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: '0.06em' }}>✓ Fully Paid</span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {[
                      { label: 'Before Placement', val: paymentHistory.beforePlacement, color: '#34d399' },
                      { label: 'After Placement',  val: paymentHistory.afterPlacement,  color: '#34d399' },
                      { label: 'Total Paid',        val: paymentHistory.totalPaid,        color: '#a78bfa' },
                      { label: 'Remaining',         val: paymentHistory.remainingBalance, color: paymentHistory.remainingBalance > 0 ? '#f87171' : '#34d399' },
                    ].map(({ label, val, color }) => (
                      <div key={label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                        <p style={{ ...lbl, marginBottom: '3px' }}>{label}</p>
                        <p style={{ color, fontSize: '1rem', fontWeight: '900', margin: 0 }}>₹{val.toLocaleString('en-IN')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Divider ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                <span style={{ ...lbl, margin: 0 }}>Personal Details</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
              </div>

              {/* ── Name ── */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><User size={13} /> Full Name</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><User size={15} /></span>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                    placeholder="Enter your full name" style={inp(errors.name)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.name ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.name && <p style={errTxt}>⚠ {errors.name}</p>}
              </div>

              {/* ── Phone ── */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Phone size={13} /> Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Phone size={15} /></span>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                    placeholder="10-digit mobile number" style={inp(errors.phone)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.phone ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.phone && <p style={errTxt}>⚠ {errors.phone}</p>}
              </div>

              {/* ── College + Degree ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Building size={13} /> College</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Building size={15} /></span>
                    <input type="text" name="collegeName" value={formData.collegeName} onChange={handleInputChange}
                      placeholder="College name" style={inp(errors.collegeName)}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                      onBlur={e => { e.target.style.borderColor = errors.collegeName ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                    />
                  </div>
                  {errors.collegeName && <p style={errTxt}>⚠ {errors.collegeName}</p>}
                </div>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><BookOpen size={13} /> Degree</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><BookOpen size={15} /></span>
                    <input type="text" name="degree" value={formData.degree} onChange={handleInputChange}
                      placeholder="e.g. B.Tech, BCA" style={inp(errors.degree)}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                      onBlur={e => { e.target.style.borderColor = errors.degree ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                    />
                  </div>
                  {errors.degree && <p style={errTxt}>⚠ {errors.degree}</p>}
                </div>
              </div>

              {/* ── Year of Passout ── */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={13} /> Year of Passout</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Calendar size={15} /></span>
                  <input type="number" name="yearOfPassout" value={formData.yearOfPassout} onChange={handleInputChange}
                    placeholder="e.g. 2025" min="1950" max="2030" style={inp(errors.yearOfPassout)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={e => { e.target.style.borderColor = errors.yearOfPassout ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {errors.yearOfPassout && <p style={errTxt}>⚠ {errors.yearOfPassout}</p>}
              </div>

              {/* ── Terms (first-time only) ── */}
              {isFirstTimeUser && (
                <div style={{ padding: '16px', borderRadius: '12px', background: errors.agreesToTerms ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.04)', border: `1.5px solid ${errors.agreesToTerms ? '#f87171' : 'rgba(255,255,255,0.1)'}`, marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <input type="checkbox" id="agreesToTerms" name="agreesToTerms" checked={formData.agreesToTerms} onChange={handleInputChange}
                      style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer', accentColor: '#F0B429', flexShrink: 0 }} />
                    <label htmlFor="agreesToTerms" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.84rem', lineHeight: 1.55, cursor: 'pointer' }}>
                      I agree to the{' '}
                      <button type="button" onClick={() => setShowTermsModal(true)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F0B429', fontWeight: '700', fontSize: '0.84rem', fontFamily: 'Poppins, sans-serif', textDecoration: 'underline', padding: 0 }}>
                        Terms & Conditions
                      </button>
                      {' '}of Vista Tech's Pay After Placement program.
                      {formData.agreesToTerms && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', color: '#34d399', fontWeight: '700', fontSize: '0.75rem' }}>
                          <Check size={12} /> Accepted
                        </span>
                      )}
                    </label>
                  </div>
                  {errors.agreesToTerms && <p style={errTxt}>⚠ {errors.agreesToTerms}</p>}
                </div>
              )}

              {/* ── Returning student notice ── */}
              {!isFirstTimeUser && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', borderRadius: '12px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: '20px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check size={16} color="white" />
                  </div>
                  <div>
                    <p style={{ color: '#fbbf24', fontWeight: '800', fontSize: '0.88rem', margin: '0 0 3px' }}>Welcome Back!</p>
                    <p style={{ color: 'rgba(251,191,36,0.7)', fontSize: '0.78rem', margin: 0 }}>Terms already accepted. This is your balance payment submission.</p>
                  </div>
                </div>
              )}

              {/* ── Payment CTA / Payment Panel / Already Paid ── */}
              {!paymentHistory?.isFullyPaid && !showPayment ? (
                <motion.button type="button" onClick={handlePaymentClick}
                  whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14', boxShadow: '0 8px 28px rgba(240,180,41,0.35)' }}
                >
                  <CreditCard size={20} />
                  {isFirstTimeUser ? 'Proceed to Payment →' : 'Pay Balance Amount →'}
                </motion.button>
              ) : paymentHistory?.isFullyPaid ? (
                <motion.button type="button" onClick={handleSubmit} disabled={isSubmitting}
                  whileHover={isSubmitting ? {} : { scale: 1.01 }} whileTap={isSubmitting ? {} : { scale: 0.98 }}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: isSubmitting ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #10b981, #059669)', color: isSubmitting ? 'rgba(255,255,255,0.3)' : 'white', boxShadow: isSubmitting ? 'none' : '0 8px 24px rgba(16,185,129,0.35)' }}
                >
                  {isSubmitting ? 'Submitting...' : <><Check size={20} /> Complete Registration (Already Paid)</>}
                </motion.button>
              ) : showPayment ? (
                // ── Payment Panel ──
                <div style={{ ...innerSection, marginTop: '4px' }}>
                  {/* header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', padding: '14px 16px', borderRadius: '10px', background: 'linear-gradient(135deg, #2b2240, #4e3a69)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IndianRupee size={17} color="white" />
                      </div>
                      <div>
                        <p style={{ color: 'white', fontWeight: '700', fontSize: '0.88rem', margin: 0 }}>Complete Payment</p>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', margin: 0 }}>Secure UPI transfer</p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', margin: 0 }}>Due</p>
                      <p style={{ color: '#F0B429', fontSize: '1.25rem', fontWeight: '900', margin: 0 }}>₹{formData.paymentAmount.toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Payment Type — returning students only */}
                  {!isFirstTimeUser && (
                    <div style={{ marginBottom: '18px' }}>
                      <label style={lbl}>Payment Type</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {[
                          { key: 'before_placement', label: 'Before Placement', fee: BEFORE_PLACEMENT_FEE, desc: 'Pay now to enroll' },
                          { key: 'after_placement',  label: 'After Placement',  fee: AFTER_PLACEMENT_FEE, desc: 'Pay after getting job' },
                        ].map(({ key, label, fee, desc }) => {
                          const active = formData.paymentType === key;
                          return (
                            <motion.button key={key} type="button" whileTap={{ scale: 0.97 }}
                              onClick={() => handlePaymentTypeChange(key as any)}
                              style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${active ? '#F0B429' : 'rgba(255,255,255,0.08)'}`, background: active ? 'rgba(240,180,41,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif' }}
                            >
                              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'rgba(255,255,255,0.55)', marginBottom: '4px' }}>{label}</div>
                              <div style={{ fontSize: '1rem', fontWeight: '900', color: '#F0B429' }}>₹{fee.toLocaleString('en-IN')}</div>
                              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{desc}</div>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Amount */}
                  <div style={{ marginBottom: '18px' }}>
                    <label style={lbl}>Payment Amount (₹)</label>
                    {isFirstTimeUser ? (
                      <div style={{ padding: '13px 16px', borderRadius: '10px', border: '1.5px solid rgba(240,180,41,0.4)', background: 'rgba(240,180,41,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: '#F0B429', fontWeight: '900', fontSize: '1.1rem' }}>₹1,000</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'rgba(240,180,41,0.75)', fontSize: '0.75rem', fontWeight: '600' }}>🔒 Fixed enrollment fee</span>
                      </div>
                    ) : (
                      <>
                        <input type="number" name="paymentAmount" value={formData.paymentAmount}
                          onChange={handleInputChange} min="1"
                          max={formData.paymentType === 'before_placement' ? BEFORE_PLACEMENT_FEE : AFTER_PLACEMENT_FEE}
                          style={inpNoIcon(errors.paymentAmount)}
                          placeholder="Enter amount"
                          onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                          onBlur={e => { e.target.style.borderColor = errors.paymentAmount ? '#f87171' : 'rgba(255,255,255,0.1)'; }}
                        />
                        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', marginTop: '5px' }}>
                          Full balance due:{' '}
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: '700' }}>
                            ₹{(formData.paymentType === 'before_placement'
                              ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
                              : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0))
                            ).toLocaleString('en-IN')}
                          </span>
                        </p>
                        {/* Balance timeline — partial payment */}
                        {(() => {
                          const expectedBalance = formData.paymentType === 'before_placement'
                            ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
                            : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0));
                          const isPartial = formData.paymentAmount > 0 && formData.paymentAmount < expectedBalance;
                          if (!isPartial) return null;
                          return (
                            <AnimatePresence>
                              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                                style={{ marginTop: '14px', padding: '14px', borderRadius: '12px', background: 'rgba(248,113,113,0.07)', border: '1px solid rgba(248,113,113,0.25)' }}
                              >
                                <label style={{ ...lbl, color: '#f87171', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <Clock size={13} />
                                  When will you pay the remaining ₹{(expectedBalance - formData.paymentAmount).toLocaleString('en-IN')}?
                                </label>
                                <div style={{ position: 'relative' }}>
                                  <select name="balancePaymentTimeline" value={formData.balancePaymentTimeline} onChange={handleInputChange}
                                    style={{ width: '100%', padding: '10px 32px 10px 12px', borderRadius: '8px', border: `1.5px solid ${errors.balancePaymentTimeline ? '#f87171' : 'rgba(248,113,113,0.3)'}`, background: '#1a1530', color: formData.balancePaymentTimeline ? 'white' : 'rgba(255,255,255,0.35)', fontSize: '0.86rem', fontFamily: 'Poppins, sans-serif', outline: 'none', cursor: 'pointer', appearance: 'none', boxSizing: 'border-box' }}
                                    onFocus={e => { e.target.style.borderColor = '#f87171'; }}
                                    onBlur={e => { e.target.style.borderColor = errors.balancePaymentTimeline ? '#f87171' : 'rgba(248,113,113,0.3)'; }}
                                  >
                                    <option value="">Select a timeline</option>
                                    <option value="Within 5 days">Within 3 days</option>
                                    <option value="Within 10 days">Within 5 days</option>
                           
                                  </select>
                                  <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', fontSize: '0.7rem' }}>▾</span>
                                </div>
                                {errors.balancePaymentTimeline && <p style={errTxt}>⚠ {errors.balancePaymentTimeline}</p>}
                              </motion.div>
                            </AnimatePresence>
                          );
                        })()}
                      </>
                    )}
                    {errors.paymentAmount && <p style={errTxt}>⚠ {errors.paymentAmount}</p>}
                  </div>

                  {/* QR + UPI */}
                  <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d0b14', fontWeight: '900', fontSize: '0.8rem' }}>G</div>
                      <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: '600', fontSize: '0.84rem' }}>Pay via GPay / Any UPI App</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                      <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginBottom: '14px' }}>
                        Scan QR to pay{' '}
                        <span style={{ color: '#F0B429', fontWeight: '900', fontSize: '1.2rem' }}>₹{formData.paymentAmount.toLocaleString('en-IN')}</span>
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                        <div style={{ padding: '10px', borderRadius: '14px', background: 'white', boxShadow: '0 4px 24px rgba(240,180,41,0.2)', border: '2px solid rgba(240,180,41,0.3)' }}>
                          <img src={qrCodeImage} alt="Payment QR Code" style={{ width: '160px', height: '160px', objectFit: 'contain', borderRadius: '6px', display: 'block' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                        <span style={{ ...lbl, margin: 0 }}>Or Copy UPI ID</span>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <div>
                          <p style={{ ...lbl, marginBottom: '2px' }}>UPI ID</p>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: '700', color: 'white' }}>9894449002@okbizaxis</span>
                        </div>
                        <motion.button type="button" onClick={copyUpiId} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.78rem', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14' }}>
                          Copy
                        </motion.button>
                      </div>
                      {/* Bank Transfer */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 10px' }}>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                        <span style={{ ...lbl, margin: 0 }}>Or Bank Transfer</span>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                      </div>
                      <div style={{ padding: '14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        {[['Account Name', 'Vista Tech', false], ['Account Number', '24340200002436', true], ['IFSC Code', 'FDRL0002434', true]].map(([label, value, copyable]) => (
                          <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ ...lbl, margin: 0 }}>{label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: '700', color: 'white' }}>{value}</span>
                              {copyable && (
                                <motion.button type="button" onClick={() => copyText(value as string, label as string)} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                                  style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.7rem', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: '#0d0b14' }}>
                                  Copy
                                </motion.button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Screenshot upload */}
                  {formData.paymentAmount > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Upload size={13} /> Upload Payment Screenshot
                      </label>
                      <div style={{ borderRadius: '10px', border: `1.5px dashed ${errors.paymentScreenshot ? '#f87171' : formData.paymentScreenshot ? '#34d399' : 'rgba(255,255,255,0.15)'}`, background: errors.paymentScreenshot ? 'rgba(248,113,113,0.05)' : formData.paymentScreenshot ? 'rgba(52,211,153,0.05)' : 'rgba(255,255,255,0.02)' }}>
                        <input type="file" accept="image/*" onChange={handleFileChange}
                          style={{ width: '100%', padding: '12px 16px', fontSize: '0.84rem', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', background: 'transparent', outline: 'none', fontFamily: 'Poppins, sans-serif', boxSizing: 'border-box' }} />
                      </div>
                      {errors.paymentScreenshot && <p style={errTxt}>⚠ {errors.paymentScreenshot}</p>}
                      {formData.paymentScreenshot && (
                        <p style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399', fontSize: '0.75rem', marginTop: '5px', fontWeight: '600' }}>
                          <Check size={13} /> {formData.paymentScreenshot.name}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Confirm payment */}
                  <motion.button type="button" onClick={() => setPaymentCompleted(true)} disabled={paymentCompleted}
                    whileHover={paymentCompleted ? {} : { scale: 1.01 }} whileTap={paymentCompleted ? {} : { scale: 0.97 }}
                    style={{ width: '100%', padding: '13px', borderRadius: '10px', border: paymentCompleted ? '1.5px solid rgba(52,211,153,0.4)' : 'none', cursor: paymentCompleted ? 'default' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: paymentCompleted ? 'rgba(52,211,153,0.12)' : 'linear-gradient(135deg, #10b981, #059669)', color: paymentCompleted ? '#34d399' : 'white', boxShadow: paymentCompleted ? 'none' : '0 6px 20px rgba(16,185,129,0.3)' }}
                  >
                    <Check size={17} />
                    {paymentCompleted ? '✓ Payment Confirmed' : 'I Have Paid — Confirm'}
                  </motion.button>
                </div>
              ) : null}

              {/* ── Final Submit ── */}
              {paymentCompleted && !paymentHistory?.isFullyPaid && (
                <motion.button type="button" onClick={handleSubmit} disabled={isSubmitting}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  whileHover={isSubmitting ? {} : { scale: 1.01 }} whileTap={isSubmitting ? {} : { scale: 0.97 }}
                  style={{ width: '100%', marginTop: '14px', padding: '14px', borderRadius: '12px', border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: isSubmitting ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #2b2240, #4e3a69)', color: isSubmitting ? 'rgba(255,255,255,0.3)' : 'white', boxShadow: isSubmitting ? 'none' : '0 8px 28px rgba(43,34,64,0.5)' }}
                >
                  {isSubmitting
                    ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Submitting...</>
                    : <><Check size={20} /> Complete Registration</>}
                </motion.button>
              )}

            </motion.div>
          </AnimatePresence>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>
              <span>🔒</span> 256-bit SSL encrypted · Your data is always secure
            </p>
            <p style={{ color: 'rgba(255,255,255,0.12)', fontSize: '0.7rem', marginTop: '5px' }}>© Vista Tech Solutions · Secure Registration Portal</p>
          </div>
        </div>
      </div>

      {showTermsModal && <TermsModal />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default StudentRegistrationForm;
